import type { ReactNode, Ref } from 'react'
import { useState } from 'react'
import useEvent from 'react-use-event-hook'
import { Tabs as BaseTabs } from '@base-ui/react/tabs'

import { cn } from '@gto/ui/cn'

import type {
  TabsColor,
  TabsIndicator,
  TabsOrientation,
  TabsPaperLevel,
  TabsSize,
} from './tabs.styles'
import type { TabsValue, TopCorners } from './tabs-context'
import { TabsContext } from './tabs-context'

export type { TabsValue } from './tabs-context'

/** Onward-typed event details from Base UI's `onValueChange` */
export type TabsChangeEventDetails = BaseTabs.Root.ChangeEventDetails

export type RootProps<Value extends string | number = string | number> = {
  /**
   * Currently selected tab value (controlled).
   * Pass `null` to deselect every tab.
   */
  value?: Value | null
  /**
   * Initial selected tab value (uncontrolled).
   * Pass `null` to mount with no tab selected.
   */
  defaultValue?: Value | null
  /**
   * Fires when the active tab changes. `eventDetails.activationDirection`
   * carries the direction of the change for callers that drive transitions
   * or analytics off it.
   *
   * `Value` is inferred from `value` / `defaultValue`, so a consumer with a
   * union-typed tab id gets that union back here without re-narrowing. The
   * boundary contract is that every rendered `Tab` carries a `Value`-typed
   * `value` — Base UI only ever reports a rendered tab's value.
   */
  onValueChange?: (value: Value, eventDetails: TabsChangeEventDetails) => void
  /** Layout flow direction */
  orientation?: TabsOrientation
  /**
   * Text colour of the selected tab's pill.
   *
   * - `primary` (default) — primary-green text
   * - `secondary` — white text
   */
  color?: TabsColor
  /** Tab height preset — `medium` (38px, default) or `large` (50px). */
  size?: TabsSize
  /**
   * When `true`, every tab stretches to share the row equally. Use for
   * small-screen / mobile layouts where the row spans the container width.
   */
  fullWidth?: boolean
  /**
   * When `true`, the `List` becomes a scroll container with chevron arrow
   * buttons at the leading and trailing edges. Use when the tab labels can
   * exceed the container's main-axis size.
   *
   * `'auto'` renders the same scroll container but shows the chevrons only
   * while the content overflows — the list's own scroll size against its
   * client size, the signal that also drives the chevrons' disabled states.
   * For strips whose fit depends on translated label widths or container
   * size rather than a breakpoint.
   *
   * Mutually exclusive with `fullWidth`: any `scrollable` value — including
   * `'auto'`, even while the tabs fit — disables `fullWidth`, because there
   * is no stretch behaviour with overflow.
   */
  scrollable?: boolean | 'auto'
  /**
   * Paper-elevation level used for the active pill background and the
   * connector flares. Pick a level from the design-system surface scale.
   * Default is `3` (matches the Figma export).
   */
  paperLevel?: TabsPaperLevel
  /**
   * Standalone pill treatment: the active indicator is fully rounded and the
   * connector flares are dropped. Use for horizontal tab strips that sit on a
   * flat surface with no attached `Panel` to merge into (the default
   * chrome-tab pill would point at nothing). No-op for vertical tabs, whose
   * pill is already fully rounded.
   */
  detached?: boolean
  /**
   * Active-indicator shape. `pill` (default) is the chrome-tab pill with
   * connector flares; `underline` marks the active tab with a 2px bar along
   * the list's bottom edge instead — for tab strips that sit on their own
   * surface header rather than merging into a `Panel`. The bar follows
   * `color`. No-op for vertical tabs, which keep the pill.
   */
  indicator?: TabsIndicator
  className?: string
  children?: ReactNode
  ref?: Ref<HTMLDivElement>
  'data-tst'?: string
}

/**
 * Tabs root — wraps base-ui's `Tabs.Root` and shares the visual config plus
 * the active value with descendant `Tab` / `List` / `Panel` via `TabsContext`.
 */
export const Root = <Value extends string | number = string | number>({
  value,
  defaultValue,
  onValueChange,
  orientation = 'horizontal',
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  scrollable = false,
  detached = false,
  indicator = 'pill',
  paperLevel = 3,
  className,
  children,
  ref,
  'data-tst': dataTestId,
}: RootProps<Value>) => {
  // Mirror base-ui's controlled/uncontrolled value so `Panel` has a stable
  // useEffect dep that fires on every tab change — that's what triggers the
  // corner re-measurement against the new active tab's bounding rect.
  // (base-ui exposes the same value via `useTabsRootContext`, but it's marked
  // `@internal` and absent from the exported parts barrel — see
  // node_modules/@base-ui/react/tabs/root/TabsRootContext.d.ts.)
  //
  // Default falls back to `0` to mirror base-ui's `defaultValueProp` exactly
  // — see node_modules/@base-ui/react/tabs/root/TabsRoot.js. The two sources
  // of truth (this mirror and base-ui's internal state) must agree on the
  // initial value; otherwise corner measurement keys off a `data-active` tab
  // whose value differs from `activeValue` in context for one frame.
  // Any scrollable value — `'auto'` included, even while the tabs fit —
  // disables `fullWidth`: there is no stretch behaviour with overflow.
  const stretchesToRow = scrollable === false && fullWidth

  const isControlled = value !== undefined
  const [uncontrolledValue, setUncontrolledValue] = useState<TabsValue>(
    defaultValue === undefined ? 0 : defaultValue,
  )
  const activeValue = isControlled ? value : uncontrolledValue

  const handleValueChange = useEvent((next: Value, details: TabsChangeEventDetails) => {
    if (!isControlled) {
      setUncontrolledValue(next)
    }
    onValueChange?.(next, details)
  })

  // Corner state lives at Root so it survives Panel-instance swaps. With
  // `keepMounted=false` (the common case), the active Panel unmounts on
  // tab change and a new Panel for the new active value mounts. If corners
  // lived inside Panel, the new instance would mount with `useState`'s
  // initial `BOTH_ROUNDED` — visibly snapping the corner shape between the
  // OLD active panel's measured state and `BOTH_ROUNDED` within the same
  // frame, before the deferred measurement on the new panel ever fires.
  // Lifting state to Root means the new Panel reads the previously
  // committed corners and renders identically to the previous frame; the
  // deferred fire then commits the new measured state at slide-end.
  // The raw pair carries the `State` suffix because the plain `setCorners`
  // name belongs to the stabilized wrapper below — the one the context exposes.
  const [cornersState, setCornersState] = useState<TopCorners>({
    topLeft: 'round',
    topRight: 'round',
  })
  // `setCorners` is wrapped in `useEvent` so the context value's identity
  // depends only on the corners snapshot — Panel's effect dep array stays
  // stable across renders.
  const setCorners = useEvent((next: TopCorners) => {
    setCornersState((previous) => {
      if (previous.topLeft === next.topLeft && previous.topRight === next.topRight) {
        return previous
      }

      return next
    })
  })

  return (
    <TabsContext
      value={{
        color,
        size,
        fullWidth: stretchesToRow,
        scrollable,
        detached,
        indicator,
        orientation,
        paperLevel,
        activeValue,
        corners: cornersState,
        setCorners,
      }}
    >
      <BaseTabs.Root
        ref={ref}
        className={cn('flex', orientation === 'vertical' ? 'flex-row' : 'flex-col', className)}
        data-tst={dataTestId}
        defaultValue={defaultValue}
        orientation={orientation}
        value={value}
        onValueChange={handleValueChange}
      >
        {children}
      </BaseTabs.Root>
    </TabsContext>
  )
}
